import { defineRouting } from "next-intl/routing";

export const LOCALES = ["en", "it"] as const;
export type AppLocale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: AppLocale = "en";

/** Type guard: is `value` one of our supported locales? */
export function isSupportedLocale(value: unknown): value is AppLocale {
  return (
    typeof value === "string" && (LOCALES as readonly string[]).includes(value)
  );
}

// Single source of truth for the NEXT_LOCALE cookie options. Consumed by
// `routing.localeCookie` below AND by the setUserLocale server action, which
// must set the cookie itself (next-intl only sets it via middleware/navigation,
// not arbitrary actions). Exporting the values once prevents the two call
// sites from drifting apart.
export const LOCALE_COOKIE = {
  name: "NEXT_LOCALE",
  maxAge: 60 * 60 * 24 * 365, // 1 year
  sameSite: "lax",
  path: "/",
  secure: process.env.NODE_ENV === "production",
} as const;

export const routing = defineRouting({
  locales: LOCALES,
  defaultLocale: DEFAULT_LOCALE,
  // EN stays unprefixed — every existing /home, /domain, /auth URL is
  // untouched (clean URLs, no broken links/SEO). IT is served under /it.
  localePrefix: "as-needed",
  // "/" must ALWAYS serve EN — never auto-redirect to /it from the
  // Accept-Language header. IT is reached only via an explicit /it link
  // or the in-app language switcher.
  localeDetection: false,
  // The cookie next-intl reads/writes to remember the chosen locale.
  // Per-user (per browser), 1 year, survives logout. See LOCALE_COOKIE.
  localeCookie: LOCALE_COOKIE,
});
